import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import { useTranslations } from 'next-intl'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { FeaturedArticlesProps } from '~/components/blocks/FeaturedArticles/FeaturedArticles'
import PageSubNavigation from '~/components/blocks/PageSubNavigation/PageSubNavigation'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import TeamExpertiseList from '~/components/blocks/TeamExpertiseList/TeamExpertiseList'
import type { TeamHighlightCardProps } from '~/components/blocks/TeamHighlightCard/TeamHighlightCard'
import TeamHighlights from '~/components/blocks/TeamHighlights/TeamHighlights'
import TeamProfileHeader from '~/components/blocks/TeamProfileHeader/TeamProfileHeader'
import TeamProfileQuote from '~/components/blocks/TeamProfileQuote/TeamProfileQuote'
import PressRelatedArticles from '~/components/press/PressRelatedArticles/PressRelatedArticles'
import { resolveBlock } from '~/components/serializers'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  expert,
  getBodyModules,
  image,
  internationalizedArrayField,
  internationalizedStringField,
  link,
  myPortfolioBanner,
} from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import toPlainText from '~/services/toPlainText'
import {
  buildInverseTranslationQuery,
  processInverseQueryResults,
} from '~/utils/articles/translationHelpers'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { metaQuery } from '~/utils/seo'
import { slugFromContext, urlFieldProps } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

export interface PageData {
  _id: string
  name: string
  magazineRole: string[]
  jobTitle: string
  phoneNumber: string
  email: string
  linkedIn: string
  description: any
  image: any
  quote: string
  author: string
  articleCount: number
  body: any
  slug: any
  breadcrumb: BreadcrumbProps['list']
  featuredArticles: FeaturedArticlesProps['articles']
  relatedArticles: ArticlePreviewCardProps[]
  videos: ArticlePreviewCardProps[]
  podcasts: ArticlePreviewCardProps[]
  featuredLogos: {
    image: any
    link: string
  }[]
  featuredTopics: { name: string; slug: string; order?: number | null }[]
  highlights: TeamHighlightCardProps[]
  highlights_raw: any
}

const TeamMember: NextPage<PageData> = (props: PageData) => {
  const { breadcrumb } = props
  const t = useTranslations('teamMember')

  const pageBlocks = props.body

  if (!props.name) return null

  const {
    _id,
    name,
    magazineRole,
    jobTitle,
    phoneNumber,
    email,
    linkedIn,
    description,
    image,
    quote,
    author,
    articleCount,
    featuredArticles,
    relatedArticles,
    slug,
    videos,
    podcasts,
    featuredLogos,
    featuredTopics,
    highlights,
    highlights_raw,
  } = props

  const headerProps = {
    _id,
    name,
    magazineRole,
    jobTitle,
    phoneNumber,
    email,
    linkedIn,
    description,
    image,
    articleCount,
    featuredLogos,
    pressArticleCount: featuredArticles?.length,
  }

  const navigationItems = [{ title: t('navigation.profile'), link: 'profile' }]

  if (highlights?.length > 0) {
    navigationItems.push({
      title: t('navigation.highlights'),
      link: 'highlights',
    })
  }

  if (featuredArticles?.length > 0) {
    navigationItems.push({
      title: t('navigation.inThePress'),
      link: 'inthepress',
    })
  }

  if (videos?.length > 0) {
    navigationItems.push({
      title: t('navigation.videos'),
      link: 'featuredvideos',
    })
  }

  if (podcasts.length > 0) {
    navigationItems.push({ title: t('navigation.podcasts'), link: 'podcasts' })
  }

  if (relatedArticles.length > 0) {
    navigationItems.push({ title: t('navigation.articles'), link: 'articles' })
  }

  return (
    <>
      <div>
        <Breadcrumb list={breadcrumb} />
        <TeamProfileHeader {...headerProps} />
        {quote && <TeamProfileQuote quote={quote} author={author} />}
      </div>
      {navigationItems.length > 1 && (
        <PageSubNavigation pageLinks={navigationItems} />
      )}
      {featuredTopics && featuredTopics.length > 0 && (
        <TeamExpertiseList
          featuredTopics={featuredTopics}
          teamMemberName={name}
        />
      )}
      {highlights && highlights.length > 0 && (
        <div id="highlights">
          <TeamHighlights highlights={highlights} />
        </div>
      )}

      {featuredArticles && featuredArticles.length > 0 && (
        <div className={styles.teamFeaturedArticles} id="inthepress">
          <PressRelatedArticles
            verticalPadding={false}
            category={name}
            title={t('sections.inThePress')}
            categoryType="author"
            articles={featuredArticles?.slice(0, 12)}
            reducedHeight
          />
        </div>
      )}
      {pageBlocks && (
        <div className={styles.body}>
          {pageBlocks.map((block: any, i: any) => resolveBlock(block, i))}
        </div>
      )}

      {videos.length > 0 && (
        <div id="featuredvideos">
          <RelatedArticles
            articles={videos}
            title={t('sections.recentVideos')}
            disableFilters
            browseButtonFilter={`author=${encodeURIComponent(name)}&filter=videos#all-articles`}
            noPaddingBottom
          />
        </div>
      )}

      {podcasts.length > 0 && (
        <div id="podcasts">
          <RelatedArticles
            articles={podcasts}
            title={t('sections.recentPodcasts')}
            disableFilters
            browseButtonFilter={`author=${encodeURIComponent(name)}&filter=podcasts#all-articles`}
            noPaddingBottom
          />
        </div>
      )}

      {relatedArticles.length > 0 && (
        <div id="articles">
          <RelatedArticles
            articles={relatedArticles}
            title={t('sections.recentArticles')}
            disableFilters
            browseButtonFilter={`author=${encodeURIComponent(name)}#all-articles`}
          />
        </div>
      )}
    </>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: { settings: any }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity(context)
    const lang = (
      Array.isArray(context?.params?.lang)
        ? context.params.lang[0]
        : context?.params?.lang || 'en'
    ) as Locale
    const messages = await loadMessages(lang)

    const slug = `team/${slugFromContext(context)}`

    // STEP 1: Pre-query to detect which body module types are actually used
    const baseSelector = createBaseSelector(
      'team',
      [`slug.current == $slug`],
      limiter,
      scorer
    )
    const detectedTypes = await detectBodyModuleTypes(
      client,
      baseSelector,
      { slug },
      ['body']
    )

    // STEP 2: Main query with only the required body modules
    const data = await client.fetch(
      `*[_type == "team" && slug.current == $slug][0] {
        ${metaQuery('team')},
        _id,
        name,
        magazineRole,
        "jobTitle": ${internationalizedStringField('jobTitle')},
        phoneNumber,
        email,
        linkedIn,
        "description": ${internationalizedArrayField('description', lang)},
        image,
        "quote": select(defined(quote) => ${internationalizedStringField('quote')}, null),
        "author": select(defined(author) => ${internationalizedStringField('author')}, null),
        "featuredTopics": featuredTopics[]-> {
          name,
          "slug": slug.current,
          "order": categoryOrder
        },
        "highlights": highlights[] {
          ...,
          "title": ${internationalizedArrayField('title', lang)},
          "subTitle": ${internationalizedStringField('subTitle')},
          "text": ${internationalizedArrayField('text', lang)},
          "linkText": ${internationalizedStringField('linkText')},
          "author": author-> {
            ${expert}
          },
          "link": {
            "text": ${internationalizedStringField('linkText')},
            launchForm,
            gaTrack,
            gaTrackEventName,
            phCTASource,
            phEventName,
            phInteractionLocation,
            phWebProduct,
            "externalLink": linkExternal,
            "internalLink": link->{
              "url": {
                ${urlFieldProps}
              }
            },
            "terms": ctaTerms,
            "target": target
          }
        },
        ${myPortfolioBanner}
        featuredLogos,
        "articleCount": count(*[_type == 'article' && author._ref == ^._id]),
        ${buildInverseTranslationQuery(
          `(author->slug.current == $slug || $slug in additionalAuthors[]->slug.current) && primaryCategory->slug.current == 'podcasts'`,
          lang,
          'podcasts',
          'date desc',
          12
        )},
        ${buildInverseTranslationQuery(
          `(author->slug.current == $slug || $slug in additionalAuthors[]->slug.current) && primaryCategory->slug.current == 'videos'`,
          lang,
          'videos',
          'date desc',
          12
        )},
        "featuredArticles": *[_type == 'featuredArticles' && expert->slug.current == $slug] | order(date desc) [0...5] {
          ...,
          ${image},
          ${link},
          "expert": expert->{
            name,
            "jobTitle": ${internationalizedStringField('jobTitle')},
            image,
            "slug": slug.current,
            email
          },
          "primaryCategory": primaryCategory-> {
            name,
            "slug": slug.current,
            "order": categoryOrder
          }
        },
        ${buildInverseTranslationQuery(
          `author._ref == ^._id`,
          lang,
          'relatedArticles',
          'date desc',
          11
        )},
        "body": body[] {
          ${getBodyModules(lang, detectedTypes)}
        }
      }`,
      {
        slug: slug,
        lang: lang,
      }
    )

    // Post-process articles for translation extraction
    if (data) {
      data.podcasts = processInverseQueryResults(data.podcasts, lang)
      data.videos = processInverseQueryResults(data.videos, lang)
      data.relatedArticles = processInverseQueryResults(
        data.relatedArticles,
        lang
      )
    }

    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      ['our-people', data.name],
      '',
      undefined,
      messages,
      lang
    )
    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []
    return {
      props: {
        url: `/${slug}`,
        ...data,
        slug,
        breadcrumb,
        breadcrumbJsonLd,
        noAnimatedTheme: true,
        hasGoogleReviews: false,
        jsonLd: {
          personJsonLd: {
            jobTitle: data.jobTitle,
            name: data.name,
            slug: slug,
            linkedIn: data.linkedIn,
            image: data.image,
            description: data.description,
          },
        },
        meta: {
          ...data.meta,
          title: data?.meta?.title
            ? data.meta.title
            : `${data.name} | MyArtBroker`,
          description: data?.meta?.description
            ? data.meta.description
            : toPlainText(data.description, true),
        },
      },
    }
  } catch (e) {
    console.log('error', e)
    return {
      props: {
        notFound: true,
      },
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default TeamMember
